import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";
import { FormProvider, useForm } from "react-hook-form";
import { stringify } from "qs";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "main/javascripts/components/button/Button";
import { css } from "@emotion/react";
import { stopBodyScrolling } from "main/javascripts/utils/ScrollUtil";
import {
  isDesktop,
  moreThanBreakpoint,
} from "main/javascripts/styles/base/responsiveStyle";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  accentColor,
  backgroundColor,
  borderColor,
} from "main/javascripts/styles/base/colorStyle";
import {
  fontSize,
  fontWeight,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import { borderRadius } from "main/javascripts/styles/base/borderStyle";
import { yupDefaultMessages } from "main/javascripts/constants/FormConstants";
import { ModalDialogWithClose } from "main/javascripts/components/modal/ModalDialogWithClose";
import { SvgIcon } from "main/javascripts/components/icon/SvgIcon";
import { CruiseSorterBlock } from "main/javascripts/components/cruise/form/CruiseSorterBlock";
import { useAppSelector } from "main/javascripts/store";
import { regionSelector, vesselSelector } from "../lpCruiseSlice";

type LpCruiseSorterFormData = {
  form?: any; // エラー用
};

yup.setLocale(yupDefaultMessages);
const schema = yup.object().shape({});

export interface IProps {
  defaultValues?: any;
}

export const LpCruiseSorterForm: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const { defaultValues } = props;
  const { t } = useTranslation(["component", "label", "button"]);
  const router = useRouter();

  const formDefaultValues = defaultValues || {
    sort_by: [
      {
        sort_name: "price_cents",
        order: "asc",
      },
    ],
  };

  const region = useAppSelector(regionSelector);
  const vessel = useAppSelector(vesselSelector);

  // TODO: 旧reducerをそのまま使用
  const breakpoint: any = useSelector<any>(
    (state) => state.breakpointReducer.breakpoint
  );

  const [isDisplayed, setIsDisplayed] = useState(false);

  const methods = useForm<LpCruiseSorterFormData>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: formDefaultValues,
  });
  const { handleSubmit, clearErrors } = methods;

  useEffect(() => {
    window.addEventListener("resize", onResizeWindow);
    return () => {
      window.removeEventListener("resize", onResizeWindow);
    };
  }, []);

  const onResizeWindow: () => void = () => {
    if (isDisplayed && isDesktop(breakpoint)) {
      stopBodyScrolling(false);
    }
  };

  const onClickFilterButton: () => void = () => {
    displayModal();
  };

  const onClickCloseButton: () => void = () => {
    hideModal();
  };

  const displayModal: () => void = () => {
    setIsDisplayed(true);
    stopBodyScrolling(true);
  };

  const hideModal: () => void = () => {
    setIsDisplayed(false);
    stopBodyScrolling(false, 0);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    clearErrors("form");
    handleSubmit(onSubmit)(e);
  };

  const onSubmit: (values: any, _: any) => void = async (
    values: any,
    _: any
  ) => {
    const sortData = values.sort_by_string.split("__");

    if (sortData.length === 2) {
      const [sortName, order] = sortData;
      const values = {
        sort_name: sortName,
        order: order,
      };

      if (region) {
        values["origin_destination_region_ids"] = [region.id];
        values["origin_destination_region_names"] = [region.name];
      }
      if (vessel) {
        values["vessel_code"] = [vessel.code];
        values["vessel_names"] = [vessel.name];
      }

      const params = stringify(values, { arrayFormat: "indices" });
      const listPagePath = `/cruises?${params}`;
      hideModal();
      // モーダルが閉じてから遷移させている
      setTimeout(() => {
        router.push(listPagePath);
      }, 400);
    }
  };

  return (
    <>
      <div css={buttonBlockStyle} onClick={onClickFilterButton}>
        <div css={buttonStyle}>
          <div css={buttonIconStyle}>
            <SvgIcon name="sort" />
          </div>
          {t("button:common.sortBy")}
        </div>
      </div>
      <ModalDialogWithClose
        displayed={isDisplayed}
        closeHandler={onClickCloseButton}
        maxWidthStyle="30rem"
        maxHeightStyle="min(30rem, 90%)"
      >
        <div css={innerStyle}>
          <div css={titleStyle}>{t("component:common.sortBy")}</div>
          <FormProvider {...methods}>
            <form css={formStyle} onSubmit={handleFormSubmit}>
              <div css={scrollBlockStyle}>
                <div css={formInnerStyle}>
                  <CruiseSorterBlock
                    hideLabel={true}
                    radioButtonStyle="large"
                  />
                </div>
              </div>
              <div css={submitBlockStyle}>
                <div css={submitButtonStyle}>
                  <Button type="submit">{t("component:search.apply")}</Button>
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
      </ModalDialogWithClose>
    </>
  );
};

const scrollBlockStyle = css`
  width: 100%;
  height: 100%;
  padding: 0 ${space.atom} 72px;
  overflow-y: auto;
  box-sizing: border-box;
`;
const buttonBlockStyle = css``;
const buttonStyle = css`
  width: 8rem;
  height: 2.5rem;
  display: grid;
  grid-template-columns: 2rem 1fr;
  align-items: center;
  justify-items: center;
  gap: 0;
  padding: ${space.atom0_5x};
  color: ${accentColor.primaryColor};
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body2};
  border: 1px solid ${accentColor.primaryColor};
  border-radius: ${borderRadius.normal};
  background: #fff;
  box-sizing: border-box;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;
  &:hover {
    color: ${accentColor.primaryDarkColor};
    border-color: ${accentColor.primaryDarkColor};
  }
`;
const buttonIconStyle = css`
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    path {
      fill: ${accentColor.primaryColor};
    }
  }
`;
const submitBlockStyle = css`
  padding: ${space.atom};
  position: absolute;
  width: 100%;
  bottom: 0;
  left: 0;
  border-top: 1px solid ${borderColor.primaryDarkColor};
  background-color: ${backgroundColor.primaryLightColor};
  box-sizing: border-box;
  ${moreThanBreakpoint("desktop")} {
    padding: ${space.atom2x};
  }
`;
const submitButtonStyle = css`
  ${moreThanBreakpoint("desktop")} {
    width: 18rem;
    margin-left: auto;
    margin-right: 0;
  }
`;
const innerStyle = css`
  margin: 0 auto;
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
`;
const formStyle = css`
  width: 100%;
`;
const formInnerStyle = css`
  padding: ${space.atom2x} 0;
`;
const titleStyle = css`
  position: absolute;
  top: -3rem;
  left: 0;
  width: 100%;
  height: 3rem;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  padding: ${space.atom} ${space.atom2x};
  font-size: ${fontSize.mediumHeading};
  font-weight: ${fontWeight.medium};
  background-color: ${backgroundColor.primaryLightColor};
  border-bottom: 1px solid ${borderColor.primaryDarkColor};
  border-top-left-radius: ${borderRadius.normal};
  border-top-right-radius: ${borderRadius.normal};
`;
